import { useCallback, useRef, useState } from "react";
// Speaks the Assistant's reply aloud via the browser's own SpeechSynthesis API, and drives the
// orb's "speaking" intensity off that utterance's real `boundary`/`start`/`end` events.
//
// Honest limitation, flagged rather than hidden: the Web Speech API gives no access to the
// synthesized audio's actual waveform/buffer (`SpeechSynthesisUtterance` doesn't go through an
// `<audio>` element or expose a `MediaStream`), so this can't drive a live amplitude level the way
// `usePollyAudio.ts` once did for real Polly audio (that approach was later removed there too --
// routing playback through a Web Audio graph turned out to cause real audio popping/cutoffs,
// confirmed live on a real phone; see that file's own doc comment). Per-word `boundary` events are
// a real signal from the actual TTS engine (not fabricated), used here to pulse the orb's intensity
// in sync with speech -- this hook is only reached as a fallback when a real Polly call fails
// outright, not the primary voice path.
export function useSpeakingPulse() {
    const [speaking, setSpeaking] = useState(false);
    const [pulse, setPulse] = useState(0);
    const pulseTimer = useRef(null);
    const speak = useCallback((text) => {
        if (!("speechSynthesis" in window))
            return;
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.onstart = () => setSpeaking(true);
        utterance.onend = () => {
            setSpeaking(false);
            setPulse(0);
        };
        utterance.onerror = () => {
            setSpeaking(false);
            setPulse(0);
        };
        utterance.onboundary = () => {
            setPulse(1);
            if (pulseTimer.current)
                clearTimeout(pulseTimer.current);
            pulseTimer.current = setTimeout(() => setPulse(0.3), 120);
        };
        window.speechSynthesis.speak(utterance);
    }, []);
    const stop = useCallback(() => {
        if ("speechSynthesis" in window)
            window.speechSynthesis.cancel();
        setSpeaking(false);
        setPulse(0);
    }, []);
    return { speaking, pulse, speak, stop, supported: typeof window !== "undefined" && "speechSynthesis" in window };
}
